import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginResponse } from '@/types/auth'
import { useNotify } from '@/composables/useNotify'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const notify = useNotify()
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.is_admin === true)
  const userName = computed(() => user.value?.display_name || user.value?.email || 'Unbekannt')

  // Actions
  const setUser = (userData: User | null) => {
    user.value = userData
    // User-Daten auch in localStorage speichern für Wiederherstellung
    if (userData) {
      localStorage.setItem('user_data', JSON.stringify(userData))
    } else {
      localStorage.removeItem('user_data')
    }
    error.value = null
  }

  const setTokens = (accessToken: string, refreshTokenValue?: string) => {
    token.value = accessToken
    if (refreshTokenValue) {
      refreshToken.value = refreshTokenValue
    }
    
    // Token im localStorage speichern für Persistenz
    localStorage.setItem('auth_token', accessToken)
    if (refreshTokenValue) {
      localStorage.setItem('refresh_token', refreshTokenValue)
    }
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    refreshToken.value = null
    error.value = null
    
    // Tokens aus localStorage entfernen
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
  }

  const setError = (errorMessage: string) => {
    error.value = errorMessage
    isLoading.value = false
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
    if (loading) {
      error.value = null
    }
  }

  // Microsoft OAuth2 Login
  const loginWithMicrosoft = async () => {
    setLoading(true)

    try {
      // Login-URL + CSRF-State vom Backend holen (Backend speichert State in DB)
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost/OdaToolsDev/api'
      const response = await fetch(`${apiBaseUrl}/v1/auth/login`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Microsoft login URL konnte nicht geladen werden.')
      }

      const data = await response.json()
      const loginUrl: string = data?.data?.login_url
      const state: string = data?.data?.state

      if (!loginUrl || !state) {
        throw new Error('Ungültige Antwort vom Auth-Server.')
      }

      // State lokal speichern für Frontend-seitige Validierung beim Callback
      sessionStorage.setItem('oauth_state', state)
      localStorage.setItem('oauth_state', state)
      console.log('[AuthStore] State vom Backend erhalten und gespeichert:', state)

      // Redirect zum Microsoft Login
      window.location.href = loginUrl

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login fehlgeschlagen'
      setError(errorMessage)
      console.error('[AuthStore] loginWithMicrosoft Fehler:', err)
    }
  }

  // OAuth2 Callback verarbeiten
  const handleOAuthCallback = async (code: string, state: string): Promise<boolean> => {
    try {
      console.log('[AuthStore] Starting OAuth callback processing...')
      setLoading(true)
      
      // State validieren (CSRF-Schutz) - prüfe beide Storage-Typen
      const sessionState = sessionStorage.getItem('oauth_state')
      const localState = localStorage.getItem('oauth_state')
      const storedState = sessionState || localState
      
      console.log('[AuthStore] State validation:', { 
        received: state, 
        sessionStorage: sessionState, 
        localStorage: localState, 
        storedState, 
        match: state === storedState 
      })
      
      if (state !== storedState) {
        throw new Error('Ungültiger OAuth-State. Möglicher CSRF-Angriff.')
      }
      
      // State aus beiden Storages entfernen
      sessionStorage.removeItem('oauth_state')
      localStorage.removeItem('oauth_state')
      
      // Backend-API für Token-Exchange aufrufen
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost/OdaToolsDev/api'
      const apiUrl = `${apiBaseUrl}/v1/auth/callback`
      console.log('[AuthStore] Making API call to:', apiUrl)
      
      const requestBody = {
        code,
        state,
        redirect_uri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI
      }
      console.log('[AuthStore] Request body:', requestBody)
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })
      
      console.log('[AuthStore] API response status:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorData = await response.text() // Use text() first to see raw response
        console.error('[AuthStore] API error response:', errorData)
        try {
          const jsonError = JSON.parse(errorData)
          throw new Error(jsonError.message || 'Login fehlgeschlagen')
        } catch {
          throw new Error(`HTTP ${response.status}: ${errorData || 'Login fehlgeschlagen'}`)
        }
      }
      
      const responseText = await response.text()
      console.log('[AuthStore] Raw API response:', responseText)
      
      let loginData: LoginResponse
      try {
        loginData = JSON.parse(responseText)
        console.log('[AuthStore] Parsed login data:', loginData)
      } catch (e) {
        console.error('[AuthStore] Failed to parse response as JSON:', e)
        throw new Error('Invalid response format from server')
      }
      
      // User und Tokens setzen
      console.log('[AuthStore] Setting user and tokens...')
      setUser(loginData.data.user)
      // TypeScript-Workaround: API response hat tokens-Objekt, aber Type-Definition erwartet flache Struktur
      const tokens = (loginData.data as any).tokens || loginData.data;
      setTokens(tokens.access_token, tokens.refresh_token)
      
      // Tools und Permissions laden nach erfolgreichem Login
      console.log('[AuthStore] Loading tools and permissions...')
      try {
        const { useToolsStore } = await import('./tools')
        const toolsStore = useToolsStore()
        await toolsStore.loadToolsAndPermissions()
        console.log('[AuthStore] Tools and permissions loaded successfully')
      } catch (toolsError) {
        console.error('[AuthStore] Error loading tools:', toolsError)
        // Tools-Fehler soll Login nicht blockieren
      }
      
      // Erfolgsmeldung anzeigen
      notify.success('Anmeldung erfolgreich', `Willkommen zurück, ${loginData.data.user.display_name || loginData.data.user.email}!`, { duration: 4000 })
      
      console.log('[AuthStore] Authentication completed successfully')
      setLoading(false)
      return true
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler beim Login'
      setError(errorMessage)
      console.error('OAuth Callback Fehler:', err)
      return false
    }
  }

  // Token Refresh
  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken.value) {
      console.log('[AuthStore] No refresh token available')
      return false
    }

    try {
      console.log('[AuthStore] Attempting to refresh access token...')
      
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost/OdaToolsDev/api'
      const response = await fetch(`${apiBaseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken.value
        })
      })

      if (!response.ok) {
        console.log('[AuthStore] Refresh token invalid or expired')
        return false
      }

      const data = await response.json()
      console.log('[AuthStore] Token refresh successful')

      // Update tokens
      const tokens = data.data.tokens || data.data
      setTokens(tokens.access_token, tokens.refresh_token)

      // Update user data if provided
      if (data.data.user) {
        setUser(data.data.user)
      }

      return true
    } catch (error) {
      console.error('[AuthStore] Error refreshing token:', error)
      return false
    }
  }

  // Logout
  const logout = async (showMessage = true) => {
    try {
      setLoading(true)
      
      // Backend über Logout informieren
      if (token.value) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost/OdaToolsDev/api'
        await fetch(`${apiBaseUrl}/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.value}`
          }
        })
      }
      
      // Kurze Ladezeit für bessere UX (1 Sekunde)
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (showMessage) {
        notify.info('Abmeldung', 'Sie wurden erfolgreich abgemeldet.')
      }
      
    } catch (err) {
      console.error('Logout error:', err)
      // Auch bei Backend-Fehlern lokalen Logout durchführen
      // Kurze Ladezeit auch bei Fehlern für konsistente UX
      await new Promise(resolve => setTimeout(resolve, 1000))
    } finally {
      clearAuth()
      setLoading(false)
      
      // Zur Startseite mit Anmeldefenster weiterleiten
      if (router.currentRoute.value.path !== '/') {
        await router.push('/')
      }
    }
  }

  // User-Profil laden
  const loadUserProfile = async (): Promise<boolean> => {
    if (!token.value) {
      return false
    }
    
    try {
      setLoading(true)
      
      const response = await fetch('http://localhost/OdaToolsDev/api/v1/auth/me', {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token abgelaufen - Session beenden
          console.log('[AuthStore] Token abgelaufen beim Laden des Benutzerprofils')
          await handleTokenExpiry()
          return false
        }
        throw new Error('Profil konnte nicht geladen werden')
      }
      
      const profileData = await response.json()
      setUser(profileData.data)
      
      setLoading(false)
      return true
      
    } catch (err) {
      console.error('Load profile error:', err)
      setError('Profil konnte nicht geladen werden')
      return false
    }
  }

  // JWT-Payload client-seitig dekodieren und Ablauf prüfen (keine Signaturprüfung nötig)
  const isTokenExpiredOrInvalid = (tokenStr: string): boolean => {
    try {
      const parts = tokenStr.split('.')
      if (parts.length !== 3) return true
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
      return (payload.exp ?? 0) < Math.floor(Date.now() / 1000)
    } catch {
      return true
    }
  }

  // Auth-Status aus localStorage wiederherstellen
  const restoreAuthState = async (): Promise<boolean> => {
    const storedToken = localStorage.getItem('auth_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedUserData = localStorage.getItem('user_data')

    if (!storedToken || !storedUserData) {
      return false
    }

    try {
      const userData = JSON.parse(storedUserData)

      // Token client-seitig prüfen bevor wir ihn setzen
      if (isTokenExpiredOrInvalid(storedToken)) {
        // Access Token abgelaufen oder ungültig — Refresh versuchen
        if (storedRefreshToken) {
          refreshToken.value = storedRefreshToken
          const refreshed = await refreshAccessToken()
          if (!refreshed) {
            clearAuth()
            return false
          }
          // Refresh erfolgreich — User-Daten wiederherstellen und weitermachen
          user.value = userData
        } else {
          clearAuth()
          return false
        }
      } else {
        // Token noch gültig — direkt setzen
        token.value = storedToken
        if (storedRefreshToken) {
          refreshToken.value = storedRefreshToken
        }
        user.value = userData
      }

      // Tools und Permissions laden nach Auth-Wiederherstellung
      try {
        const { useToolsStore } = await import('./tools')
        const toolsStore = useToolsStore()
        await toolsStore.loadToolsAndPermissions()
      } catch (toolsError) {
        console.error('[AuthStore] Error loading tools after restore:', toolsError)
      }

      return true

    } catch (err) {
      console.error('[AuthStore] Error restoring auth state:', err)
      clearAuth()
      return false
    }
  }

  // Token-Expiry-Handling - Weiterleitung zur Startseite (wird nur einmal ausgeführt)
  let expiryHandled = false
  const handleTokenExpiry = async () => {
    if (expiryHandled) return
    expiryHandled = true

    clearAuth()
    notify.error('Sitzung abgelaufen', 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.', { duration: 5000 })

    if (router.currentRoute.value.path !== '/') {
      await router.push('/')
    }

    // Guard nach kurzer Zeit zurücksetzen, damit ein neuer Login-Versuch später wieder greift
    setTimeout(() => { expiryHandled = false }, 10000)
  }

  // Erkennt wenn auth_token aus einem laufenden Tab aus dem localStorage entfernt wird
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === 'auth_token' && !event.newValue && token.value) {
        handleTokenExpiry()
      }
    })
  }

  return {
    // State
    user,
    token,
    refreshToken,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    isAdmin,
    userName,
    
    // Actions
    setUser,
    setTokens,
    clearAuth,
    setError,
    setLoading,
    loginWithMicrosoft,
    handleOAuthCallback,
    refreshAccessToken,
    logout,
    loadUserProfile,
    restoreAuthState,
    handleTokenExpiry
  }
})
